import jsPDF from 'jspdf';
import { ProformaInvoice, COMPANY_DATA } from '../types/proformaInvoice';

// Dodajemy obsługę czcionek z polskimi znakami
import 'jspdf-autotable';

// Deklaracja dla rozszerzonej funkcjonalności jsPDF
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

// Funkcja pomocnicza do konwersji liczby na słowa
export const numberToWords = (num: number): string => {
  const ones = ['', 'jeden', 'dwa', 'trzy', 'cztery', 'pięć', 'sześć', 'siedem', 'osiem', 'dziewięć'];
  const teens = ['dziesięć', 'jedenaście', 'dwanaście', 'trzynaście', 'czternaście', 'piętnaście', 'szesnaście', 'siedemnaście', 'osiemnaście', 'dziewiętnaście'];
  const tens = ['', '', 'dwadzieścia', 'trzydzieści', 'czterdzieści', 'pięćdziesiąt', 'sześćdziesiąt', 'siedemdziesiąt', 'osiemdziesiąt', 'dziewięćdziesiąt'];
  const hundreds = ['', 'sto', 'dwieście', 'trzysta', 'czterysta', 'pięćset', 'sześćset', 'siedemset', 'osiemset', 'dziewięćset'];

  if (num === 0) return 'zero';

  const intPart = Math.floor(num);
  const decimalPart = Math.round((num - intPart) * 100);

  let words = '';

  // Tysiące
  if (intPart >= 1000) {
    const thousandsPart = Math.floor(intPart / 1000);
    if (thousandsPart === 1) {
      words += 'tysiąc ';
    } else if (thousandsPart >= 2 && thousandsPart <= 4) {
      words += ones[thousandsPart] + ' tysiące ';
    } else if (thousandsPart >= 5 && thousandsPart <= 9) {
      words += ones[thousandsPart] + ' tysięcy ';
    } else if (thousandsPart >= 10 && thousandsPart <= 19) {
      words += teens[thousandsPart - 10] + ' tysięcy ';
    } else {
      const t = Math.floor(thousandsPart / 10);
      const o = thousandsPart % 10;
      words += tens[t] + (o > 0 ? ' ' + ones[o] : '') + ' tysięcy ';
    }
  }

  // Setki
  const hundredsPart = Math.floor((intPart % 1000) / 100);
  if (hundredsPart > 0) {
    words += hundreds[hundredsPart] + ' ';
  }

  // Dziesiątki i jedności
  const tensPart = Math.floor((intPart % 100) / 10);
  const onesPart = intPart % 10;

  if (tensPart === 1) {
    words += teens[onesPart] + ' ';
  } else {
    if (tensPart > 0) words += tens[tensPart] + ' ';
    if (onesPart > 0) words += ones[onesPart] + ' ';
  }

  words += 'złotych';

  // Grosze
  if (decimalPart > 0) {
    words += ' ' + decimalPart + '/100';
  }

  return words.trim();
};

export const generateProformaInvoicePDF = (invoice: ProformaInvoice): jsPDF => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    putOnlyUsedFonts: true,
    floatPrecision: 16
  });

  // Próba ustawienia UTF-8
  doc.setFont('helvetica', 'normal');
  
  let yPosition = 15;

  // Funkcja do skracania długich nazw
  const shortenCompanyName = (name: string): string => {
    // Jeśli nazwa zawiera pełną nazwę firmy, skróć ją maksymalnie
    if (name.includes('PRZEDSIEBIORSTWO') || 
        name.includes('PRZEDSIBIORSTWO') ||
        name.includes('PLEXI SYSTEM') ||
        name.includes('SPOLKA CYWILNA') ||
        name.includes('SPÓŁKA CYWILNA') ||
        name.includes('HANDLOWO USAUGOWE') ||
        name.includes('HANDLOWO USLUGOWE') ||
        name.length > 30) {
      return 'PPHU "PLEXI SYSTEM" S.C.';
    }
    return name;
  };

  // Helper functions to handle both old and new data structures
  const getBuyerName = () => shortenCompanyName(invoice.clientName || invoice.buyer?.name || '');
  const getBuyerAddress = () => invoice.clientAddress || invoice.buyer?.address || '';
  const getBuyerPostalCode = () => invoice.clientPostalCode || invoice.buyer?.postalCode || '';
  const getBuyerCity = () => invoice.clientCity || invoice.buyer?.city || '';
  const getBuyerNIP = () => invoice.clientNIP || invoice.buyer?.nip || '';
  const getGrossTotal = () => invoice.totalGross || invoice.summary?.grossTotal || 0;
  const getNetTotal = () => invoice.totalNet || invoice.summary?.netTotal || 0;
  const getVatTotal = () => invoice.totalVat || invoice.summary?.vatTotal || 0;

  // Kolory
  const primaryColor = { r: 255, g: 102, b: 0 }; // Pomarańczowy
  const darkGray = { r: 60, g: 60, b: 60 };
  const lightGray = { r: 245, g: 245, b: 245 };

  // Nagłówek z tłem
  doc.setFillColor(255, 102, 0); // Pomarańczowy
  doc.rect(0, 0, 210, 40, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(26);
  doc.setFont('helvetica', 'bold');
  doc.text('FAKTURA PROFORMA', 105, yPosition + 8, { align: 'center' });
  
  yPosition += 18;
  doc.setFontSize(16);
  doc.setFont('helvetica', 'normal');
  doc.text('Nr ' + invoice.invoiceNumber, 105, yPosition, { align: 'center' });
  
  // Reset koloru tekstu
  doc.setTextColor(0, 0, 0);

  // Dane firmy (lewa strona)
  yPosition = 50;
  doc.setFillColor(245, 245, 245);
  doc.rect(15, yPosition - 5, 85, 10, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('SPRZEDAWCA', 57.5, yPosition + 2, { align: 'center' });
  
  yPosition += 12;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(COMPANY_DATA.name, 20, yPosition);
  yPosition += 6;
  doc.text(COMPANY_DATA.address, 20, yPosition);
  yPosition += 6;
  doc.text(COMPANY_DATA.postalCode + ' ' + COMPANY_DATA.city, 20, yPosition);
  yPosition += 6;
  doc.text('NIP: ' + COMPANY_DATA.nip, 20, yPosition);
  yPosition += 6;
  doc.text('REGON: ' + COMPANY_DATA.regon, 20, yPosition);

  // Dane nabywcy (prawa strona)
  yPosition = 50; // Wyrównanie do poziomu sprzedawcy
  doc.setFillColor(245, 245, 245);
  doc.rect(110, yPosition - 5, 85, 10, 'F');
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('NABYWCA', 152.5, yPosition + 2, { align: 'center' });
  
  yPosition += 12;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  
  // Użyj skróconej nazwy
  const buyerName = getBuyerName();
  doc.text(buyerName, 115, yPosition);
  yPosition += 6;
  
  doc.text(getBuyerAddress(), 115, yPosition);
  yPosition += 6;
  doc.text(getBuyerPostalCode() + ' ' + getBuyerCity(), 115, yPosition);
  const buyerNIP = getBuyerNIP();
  if (buyerNIP) {
    yPosition += 6;
    doc.text('NIP: ' + buyerNIP, 115, yPosition);
  }

  // Daty
  yPosition += 20;
  doc.setFontSize(10);
  doc.text('Data wystawienia: ' + new Date(invoice.issueDate).toLocaleDateString('pl-PL'), 20, yPosition);
  yPosition += 6;
  doc.text('Data sprzedazy: ' + new Date(invoice.saleDate || invoice.issueDate).toLocaleDateString('pl-PL'), 20, yPosition);
  yPosition += 6;
  doc.text('Termin platnosci: ' + new Date(invoice.paymentDeadline).toLocaleDateString('pl-PL'), 20, yPosition);
  if (invoice.validUntil) {
    yPosition += 6;
    doc.text('Waznosc oferty: ' + new Date(invoice.validUntil).toLocaleDateString('pl-PL'), 20, yPosition);
  }

  // Tabela z pozycjami - używamy autoTable dla lepszej obsługi polskich znaków
  yPosition += 15;
  
  const tableData = invoice.items.map((item, index) => {
    const itemName = item.description || item.name || '';
    const itemUnit = item.unit || 'szt.';
    const itemUnitPrice = item.unitPrice || item.netPrice || 0;
    const itemNetValue = item.netAmount || item.netValue || (item.quantity * itemUnitPrice);
    const itemVatValue = item.vatAmount || item.vatValue || (itemNetValue * (item.vatRate / 100));
    const itemGrossValue = item.grossAmount || item.grossValue || (itemNetValue + itemVatValue);

    return [
      (index + 1).toString(),
      itemName,
      item.quantity.toString(),
      itemUnit,
      itemUnitPrice.toFixed(2) + ' zl',
      item.vatRate === -1 ? 'zw.' : item.vatRate + '%',
      itemNetValue.toFixed(2) + ' zl',
      itemVatValue.toFixed(2) + ' zl',
      itemGrossValue.toFixed(2) + ' zl'
    ];
  });

  // Używamy autoTable jeśli jest dostępna
  if (doc.autoTable) {
    doc.autoTable({
      startY: yPosition,
      head: [['Lp.', 'Nazwa', 'Ilosc', 'Jedn.', 'Cena netto', 'VAT', 'Wartosc\nnetto', 'Wartosc\nVAT', 'Wartosc\nbrutto']],
      body: tableData,
      styles: {
        font: 'helvetica',
        fontSize: 9,
        cellPadding: 2,
        lineColor: [200, 200, 200],
        lineWidth: 0.1
      },
      headStyles: {
        fillColor: [240, 240, 240],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 9
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 42 },
        2: { cellWidth: 15, halign: 'center' },
        3: { cellWidth: 15, halign: 'center' },
        4: { cellWidth: 25, halign: 'right' },
        5: { cellWidth: 15, halign: 'center' },
        6: { cellWidth: 24, halign: 'right' },
        7: { cellWidth: 24, halign: 'right' },
        8: { cellWidth: 30, halign: 'right' }
      },
      theme: 'grid'
    });
    
    yPosition = (doc as any).lastAutoTable.finalY + 10;
  } else {
    // Fallback - rysowanie tabeli ręcznie
    const tableHeaders = ['Lp.', 'Nazwa', 'Ilosc', 'Jedn.', 'Cena netto', 'VAT', 'Wartosc\nnetto', 'Wartosc\nVAT', 'Wartosc\nbrutto'];
    const columnWidths = [10, 42, 15, 15, 25, 15, 24, 24, 30];
    const startX = 10;
    
    // Rysuj nagłówki
    doc.setFont('helvetica', 'bold');
    doc.setFillColor(240, 240, 240);
    doc.rect(startX, yPosition - 5, 195, 12, 'F');
    
    doc.setFontSize(9);
    let currentX = startX;
    tableHeaders.forEach((header, index) => {
      const lines = header.split('\n');
      let headerY = yPosition;
      lines.forEach((line, lineIndex) => {
        doc.text(line, currentX + columnWidths[index]/2, headerY + (lineIndex * 4), { 
          align: 'center',
          maxWidth: columnWidths[index] - 2 
        });
      });
      currentX += columnWidths[index];
    });

    // Rysuj pozycje
    yPosition += 14;
    doc.setFont('helvetica', 'normal');
    
    tableData.forEach((row) => {
      if (yPosition > 245) {
        doc.addPage();
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        yPosition = 20;
      }

      currentX = startX;
      row.forEach((cell, index) => {
        doc.text(cell, currentX + 2, yPosition, { maxWidth: columnWidths[index] - 4 });
        currentX += columnWidths[index];
      });
      
      yPosition += 8;
      doc.setDrawColor(230, 230, 230);
      doc.line(startX, yPosition - 2, startX + 195, yPosition - 2);
    });
    
    yPosition += 5;
  }

  // Podsumowanie
  const summaryX = 125;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Razem netto:', summaryX, yPosition);
  doc.text(getNetTotal().toFixed(2) + ' zl', 190, yPosition, { align: 'right' });
  
  yPosition += 7;
  doc.text('VAT:', summaryX, yPosition);
  doc.text(getVatTotal().toFixed(2) + ' zl', 190, yPosition, { align: 'right' });
  
  yPosition += 7;
  doc.setFontSize(12);
  doc.setFillColor(255, 102, 0);
  doc.rect(120, yPosition - 5, 75, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.text('Do zaplaty:', summaryX + 5, yPosition + 2);
  doc.text(getGrossTotal().toFixed(2) + ' zl', 185, yPosition + 2, { align: 'right' });
  doc.setTextColor(0, 0, 0);

  // Słownie
  yPosition += 18;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const grossTotalInWords = invoice.summary?.grossTotalInWords || numberToWords(getGrossTotal());
  doc.text('Slownie: ' + grossTotalInWords, 20, yPosition);

  // Sposób płatności
  yPosition += 8;
  const paymentMethodText = invoice.paymentMethod === 'transfer' || invoice.paymentMethod === 'bank_transfer' ? 'przelew' : 
                          invoice.paymentMethod === 'cash' ? 'gotowka' : invoice.paymentMethod || '';
  doc.text('Sposob platnosci: ' + paymentMethodText, 20, yPosition);

  // Dane bankowe (jeśli przelew)
  if (invoice.paymentMethod === 'transfer' || invoice.paymentMethod === 'bank_transfer') {
    yPosition += 10;
    
    // Tło dla danych bankowych
    doc.setFillColor(245, 250, 255);
    doc.rect(20, yPosition - 5, 170, 35, 'F');
    doc.setDrawColor(200, 220, 240);
    doc.setLineWidth(0.5);
    doc.rect(20, yPosition - 5, 170, 35);
    
    yPosition += 3;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Dane do przelewu:', 25, yPosition);
    
    yPosition += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(COMPANY_DATA.bank.name, 25, yPosition);
    
    yPosition += 7;
    doc.setFont('helvetica', 'bold');
    doc.text(COMPANY_DATA.bank.accountNumber, 25, yPosition);
    
    yPosition += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('SWIFT: ' + COMPANY_DATA.bank.swift, 25, yPosition);
  }

  // Uwagi
  if (invoice.notes) {
    yPosition += 15;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Uwagi:', 20, yPosition);
    yPosition += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    
    // Podziel uwagi na linie
    const noteWords = invoice.notes.split(' ');
    const noteLines = [];
    let currentNoteLine = '';
    
    noteWords.forEach((word) => {
      const testLine = currentNoteLine + (currentNoteLine ? ' ' : '') + word;
      if (doc.getTextWidth(testLine) > 170 && currentNoteLine) {
        noteLines.push(currentNoteLine);
        currentNoteLine = word;
      } else {
        currentNoteLine = testLine;
      }
    });
    
    if (currentNoteLine) {
      noteLines.push(currentNoteLine);
    }
    
    noteLines.forEach(line => {
      doc.text(line, 20, yPosition);
      yPosition += 5;
    });
  }

  // Stopka
  const pageHeight = 297; // A4 height in mm
  yPosition = pageHeight - 15;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100, 100, 100);
  doc.text('Niniejszy dokument nie stanowi faktury VAT w rozumieniu przepisow ustawy o VAT', 105, yPosition, { align: 'center' });

  // Zapisz PDF
  doc.save('Faktura_Proforma_' + invoice.invoiceNumber.replace(/\//g, '_') + '.pdf');
  
  // Zwróć obiekt PDF
  return doc;
};

// Funkcja do walidacji NIP
export const validateNIP = (nip: string): boolean => {
  const nipWithoutDashes = nip.replace(/-/g, '');
  
  if (nipWithoutDashes.length !== 10) {
    return false;
  }

  const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
  let sum = 0;

  for (let i = 0; i < 9; i++) {
    sum += parseInt(nipWithoutDashes[i]) * weights[i];
  }

  const checkSum = sum % 11;
  const lastDigit = parseInt(nipWithoutDashes[9]);

  return checkSum === lastDigit;
};

// Version: 4.0.0 - Removed Polish character replacement, added autoTable support
